import React, { Component, FC } from 'react';
import { connect } from 'react-redux';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import cn from 'classnames';
import Dialog from 'components/Dialog';
import { Button, LoaderSpinner } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { apiErrorHandler } from 'core/errors';
import { DATE_FORMAT } from 'globals/constants';
import { API, getCancelToken, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { pluralize } from 'utils';

interface GetEnrolledStudentsResponse extends MessageResponse {
  students: EnrolledStudent[];
}

interface EnrolledStudentsStatus extends MessageResponse {
  importStatus: 'inprogress' | 'done';
  enrolments: EnrolledStudent[];
  removed: EnrolledStudent[];
  newAdditions: EnrolledStudent[];
}

interface EnrolledStudentsProps extends ToastManagerProp {
  getAccess: SessionData | null;
}

interface EnrolledStudentsState {
  loading: boolean;
  accounts: EnrolledStudent[];
  showSyncEnrolledStudentDialog: boolean;
  isSyncingStudents: boolean;
  syncEnrolledStudentStatus: EnrolledStudentsStatus | null;
}

class EnrolledStudents extends Component<EnrolledStudentsProps, EnrolledStudentsState> {
  apiSources = getCancelToken();

  state: EnrolledStudentsState = {
    accounts: [],
    loading: true,
    showSyncEnrolledStudentDialog: false,
    syncEnrolledStudentStatus: null,
    isSyncingStudents: false,
  };

  async getEnrolledStudents(courseId: string) {
    const response = await request<GetEnrolledStudentsResponse>({
      url: API.COURSE_ENROLMENTS(courseId),
      cancelToken: this.apiSources.token,
    });
    return response.students;
  }

  getEnrolledStudentsSyncStatus() {
    return request<EnrolledStudentsStatus>({
      url: API.COURSE_STUDENTS_SYNC_STATUS,
      cancelToken: this.apiSources.token,
    });
  }

  async syncEnrolledStudents(courseId: string) {
    return request<EnrolledStudentsStatus>({
      method: 'patch',
      url: API.COURSE_STUDENTS_SYNC,
      data: { courseId },
      cancelToken: this.apiSources.token,
    });
  }

  async componentDidMount() {
    this.apiSources = getCancelToken();
    try {
      let accounts: EnrolledStudent[] = [];
      const courseId = this.props.getAccess?.courseDetails.courseId;
      if (courseId) {
        accounts = await this.getEnrolledStudents(courseId);
      }
      this.setState({ accounts, loading: false });
    } catch (err: any) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ loading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  getTable = () => {
    const { loading } = this.state;

    if (loading) {
      return (
        <div className='loader-wrapper'>
          <LoaderSpinner />
        </div>
      );
    }

    return (
      <table className='table table--bordered'>
        <TransitionGroup component='tbody'>
          {this.state.accounts.map((account) => (
            <CSSTransition
              key={account.userId}
              classNames='removeFromList'
              timeout={{ enter: 60, exit: 300 }}
            >
              <tr>
                <td>
                  <div className='table__cell'>
                    <div className='table__cell-header table__cell-header--title'>{account.name}</div>
                    <div className='table__cell-value'>{account.emailId}</div>
                  </div>
                </td>
                <td>
                  <div className='table__cell'>
                    <div className='table__cell-header'>JOINED ON</div>
                    <div className='table__cell-value table__cell-value--no-wrap'>
                      {account.joinedOn > 0 ? dt.format(account.joinedOn, DATE_FORMAT) : '--'}
                    </div>
                  </div>
                </td>
                <td>
                  <div className='table__cell'>
                    <div className='table__cell-header'>LAST ACTIVE ON</div>
                    <div className='table__cell-value table__cell-value--no-wrap'>
                      {account.lastActive > 0 ? dt.format(account.lastActive, DATE_FORMAT) : '--'}
                    </div>
                  </div>
                </td>
              </tr>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </table>
    );
  };

  showSyncEnrollmentsDialog = async () => {
    if (this.state.isSyncingStudents) return;
    this.setState({ showSyncEnrolledStudentDialog: true, syncEnrolledStudentStatus: null });
    const data = await this.getEnrolledStudentsSyncStatus();
    this.setState({ syncEnrolledStudentStatus: data });
  };

  syncData = async () => {
    const courseId = this.props.getAccess?.courseDetails.courseId;
    if (this.state.isSyncingStudents || !courseId) return;

    this.setState({ isSyncingStudents: true, showSyncEnrolledStudentDialog: false });
    const { newAdditions, removed } = await this.syncEnrolledStudents(courseId);
    const removedStudentIds = new Set(removed.map((s) => s.userId));

    const accounts = [
      ...this.state.accounts.filter((s) => !removedStudentIds.has(s.userId)),
      ...newAdditions,
    ];

    this.setState({ accounts, isSyncingStudents: false });
  };

  syncEnrolledStudentDialog = () => {
    const { syncEnrolledStudentStatus } = this.state;
    return (
      <Dialog
        isOpen={this.state.showSyncEnrolledStudentDialog}
        onClose={() => this.setState({ showSyncEnrolledStudentDialog: false })}
        title='Sync enrolled students'
        footer={
          <>
            <Button
              variant='text'
              color='info'
              size='small'
              onClick={() => this.setState({ showSyncEnrolledStudentDialog: false })}
              preventHoverStyles
            >
              Close
            </Button>
            <Button variant='text' color='info' size='small' onClick={this.syncData} preventHoverStyles>
              Sync
            </Button>
          </>
        }
      >
        {syncEnrolledStudentStatus === null ? (
          <div className='loader-wrapper'>
            <LoaderSpinner />
          </div>
        ) : (
          <>
            <SyncTable
              title='Removed students'
              background='sync-table__wrapper--red'
              students={syncEnrolledStudentStatus.removed}
            />
            <SyncTable
              title='New students'
              background='sync-table__wrapper--green'
              students={syncEnrolledStudentStatus.newAdditions}
            />
            <SyncTable
              title='Enrolled students'
              background='sync-table__wrapper--blue'
              students={syncEnrolledStudentStatus.enrolments}
            />
          </>
        )}
      </Dialog>
    );
  };

  render() {
    const { loading, accounts } = this.state;
    const activeStudents = accounts.filter((a) => a.joinedOn !== undefined && a.joinedOn > 0).length;

    return (
      <div className='section'>
        <div className='section__header'>
          <h3 className='section__title'>
            {loading
              ? 'Loading student accounts...'
              : !activeStudents
              ? `None of the ${accounts.length} enrolled ${pluralize(
                  accounts.length,
                  'student'
                )} active on Acadly yet`
              : `${activeStudents} of the ${accounts.length} enrolled ${pluralize(
                  accounts.length,
                  'student'
                )} active on Acadly`}
          </h3>
          <Button
            variant='outlined'
            color='info'
            size='large'
            className='sync-button'
            onClick={this.showSyncEnrollmentsDialog}
          >
            {this.state.isSyncingStudents ? 'Sync in progress' : 'Sync'}
          </Button>
        </div>
        <div className='section__body'>{this.getTable()}</div>
        {this.syncEnrolledStudentDialog()}
      </div>
    );
  }
}

interface SyncTableProps {
  title: string;
  background: 'sync-table__wrapper--blue' | 'sync-table__wrapper--green' | 'sync-table__wrapper--red';
  students: EnrolledStudent[];
}

const SyncTable: FC<SyncTableProps> = ({ title, background, students = [] }) => {
  return (
    <div className={cn('sync-table__wrapper', background)}>
      <div className='sync-table__title'>{title}</div>
      <table className='table sync-table'>
        <thead>
          <tr>
            <th className='table__cell table__cell--left'>Name</th>
            <th className='table__cell table__cell--left'>Email</th>
            <th className='table__cell table__cell--center'>Joined On</th>
            <th className='table__cell table__cell--center'>Last Active On</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.userId}>
              <td className='table__cell table__cell--left'>{s.name}</td>
              <td className='table__cell table__cell--left'>{s.emailId}</td>
              <td className='table__cell table__cell--center'>
                {s.joinedOn > 0 ? dt.format(s.joinedOn, DATE_FORMAT) : '--'}
              </td>
              <td className='table__cell table__cell--center'>
                {s.lastActive > 0 ? dt.format(s.lastActive, DATE_FORMAT) : '--'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  getAccess: state.getAccess,
});

export default connect(mapStateToProps)(withToastManager(EnrolledStudents));
